import { HttpParams, httpResource } from '@angular/common/http';
import {
  afterRenderEffect,
  Component,
  computed,
  inject,
  Injector,
  input,
  OnInit,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { EWidget } from 'scw-digital-boards-widgets';
import { IDropdownOption, MatDropdown } from '../../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../../../../../shared/components/mat-dropdown/scw-mat-dropdown.interface';
import { IGetManyResponse } from '../../../../../utilities/http-utilities.service';
import { IFormAutofill } from '../../../../settings/data-integrations/form-field-mapping/form-autofill.interface';
import { EParamLocation } from '../../../../settings/filter-settings/endpoint-parameters/endpoint-parameters.interface';

@Component({
  imports: [MatDropdown],
  selector: 'app-endpoint-parameter-dropdown',
  templateUrl: './endpoint-parameter-dropdown.component.html',
})
export class EndpointParameterDropdownComponent implements OnInit {
  private readonly translate = inject(TranslateService);
  public readonly formControl = input.required<FormControl<IDropdownOption[]>>();
  public readonly selectedIdOnEdit = input.required<number | null>();
  public readonly isFormSubmitted = input.required<boolean>();
  public readonly dashboardId = input<number | null>(null);
  public readonly workspaceId = input<number | null>(null);

  public readonly searchValue = signal('');
  public readonly widgetType = input<EWidget | null>(null);

  private readonly allowedParamLocations = computed(() => {
    if (this.widgetType() === EWidget.bigNumber) {
      return [EParamLocation.query, EParamLocation.body, EParamLocation.header];
    }

    return [EParamLocation.query];
  });

  public readonly formDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: true,
    hasDelayedDefaultValue: true,
    isClientSide: false,
    repositionDropdownList: true,
    singleSelection: false,
    text: this.translate.instant('page.widgetContent.addEditDialog.endpointParameter'),
  };

  public readonly resource = httpResource<IGetManyResponse<IFormAutofill>>(() => {
    return {
      method: 'GET',
      params: new HttpParams()
        .set('limit', 1000)
        .set(
          's',
          JSON.stringify({
            $and: [
              { name: { contL: this.searchValue() } },
              { workspaceId: this.workspaceId() },
              { paramLocation: { $in: this.allowedParamLocations() } },
            ],
          }),
        )
        .set('sort', 'name,ASC'),
      reportProgress: true,
      url: 'url-params-templates',
    };
  });

  public readonly dropdownOptions = computed(() => {
    return this.resource.value()?.data.map((item) => ({ id: item.id, name: item.name })) ?? [];
  });

  public readonly isLoading = computed(() => this.resource.status() !== 'resolved');

  constructor(private readonly injector: Injector) {
    afterRenderEffect(() => {
      const control: FormControl<IDropdownOption[]> = this.formControl();
      control.enable({ emitEvent: false });
    });
  }

  public ngOnInit(): void {
    runInInjectionContext(this.injector, () =>
      toSignal(this.formControl().valueChanges, {
        initialValue: this.formControl().value,
      }),
    );
  }
}
